/**
 * WalkthroughMode - Guided Video Walkthrough for KATLAS Governance Demo
 * 
 * A recording-friendly guided flow through the full demo that explains
 * the business story clearly for non-technical viewers.
 * 
 * Features:
 * - Scene-by-scene narration panels
 * - Next/Previous navigation
 * - Autoplay with pause control
 * - Visual highlights for key sections
 * - Clean, executive-friendly presentation
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  X,
  Layers,
  Lightbulb,
  FileCheck,
  Users,
  GitBranch,
  Rocket,
  Train,
  Heart,
  Scale,
  Shield,
  Lock,
  CheckCircle2,
  AlertTriangle,
  Eye,
  Package,
  Receipt
} from 'lucide-react';
import { Button } from '../ui/button';

// Scene definitions with narration content
const SCENES = [
  {
    id: 'opening',
    title: 'Three Workflows, One Governance Substrate',
    section: 'landing',
    icon: Layers,
    narration: 'Welcome to the KATLAS Governance Demo. This demonstration shows how a single runtime governance pattern supports multiple real-world workflows across Travel, Healthcare, and Legal domains.',
    highlight: 'hero',
    duration: 8000
  },
  {
    id: 'delivery-chain-intro',
    title: 'The Authorised Delivery Chain',
    section: 'delivery-chain',
    icon: GitBranch,
    narration: 'Most organisations can track tasks. Far fewer can prove how an idea became an authorised requirement, how work was delegated, what changed, and why a production release was allowed.',
    highlight: 'delivery-ribbon',
    duration: 10000
  },
  {
    id: 'delivery-stages',
    title: 'Five Governance Stages',
    section: 'delivery-chain',
    icon: FileCheck,
    narration: 'The delivery chain tracks five stages: Idea Logged, Requirement Authorised, Work Delegated, Change Governed, and Release Allowed. Each stage records who acted, under what authority, and with what evidence.',
    highlight: 'delivery-stages',
    duration: 10000
  },
  {
    id: 'travel-intro',
    title: 'Assisted Travel Advisor',
    section: 'travel-demo',
    icon: Train,
    narration: 'The Assisted Travel Advisor protects vulnerable passengers during service disruptions. Let\'s see how KATLAS governance ensures safe recommendations while blocking unsafe advice.',
    highlight: 'travel-card',
    duration: 8000
  },
  {
    id: 'travel-outcomes',
    title: 'Better Decision, Reduced Risk',
    section: 'travel-demo',
    icon: CheckCircle2,
    narration: 'Three business outcomes define success: Better Decision quality through governed sources, Reduced Risk by blocking unverified advice, and Attributable Evidence for every recommendation.',
    highlight: 'travel-outcomes',
    duration: 9000
  },
  {
    id: 'scenario-lab',
    title: 'Scenario Lab',
    section: 'scenario-lab',
    icon: Users,
    narration: 'The Scenario Lab allows bounded scenarios to be configured and re-run. This is repeatable partner testing with governance controls, not free-text prompting. Compare runs and view receipt history for audit trails.',
    highlight: 'scenario-section',
    duration: 10000
  },
  {
    id: 'essential-baggage',
    title: 'Essential Baggage & Custody',
    section: 'baggage',
    icon: Package,
    narration: 'The Essential Baggage workflow demonstrates custody chain tracking. See exactly where items are, who has custody, and the complete handoff history with cryptographic receipts.',
    highlight: 'baggage-section',
    duration: 9000
  },
  {
    id: 'who-sees-what',
    title: 'Who Sees What',
    section: 'baggage',
    icon: Eye,
    narration: 'Bounded disclosure is preserved by role. Different actors see only what their role authorises. Medical staff see medication details. Handlers see transport requirements. Privacy boundaries are enforced at runtime.',
    highlight: 'visibility-section',
    duration: 10000
  },
  {
    id: 'other-workflows',
    title: 'Healthcare & Legal Workflows',
    section: 'other-demos',
    icon: Heart,
    narration: 'The same governance substrate powers other domains. The Patient Advisor governs healthcare referrals. Brief & Advocate Allocation governs legal case preparation. Same pattern, different actors and rules.',
    highlight: 'other-cards',
    duration: 9000
  },
  {
    id: 'closing',
    title: 'Runtime Governance',
    section: 'closing',
    icon: Shield,
    narration: 'KATLAS provides runtime Custody, Authority, and Receipts. Across idea, execution, decision, handoff, and release — every action is governed, every decision is attributable, and every handoff produces evidence.',
    highlight: 'closing',
    duration: 10000
  }
];

// Progress bar component
const ProgressBar = ({ current, total, isPlaying, progress }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-slate-400 font-mono">
      {current + 1} / {total}
    </span>
    <div className="flex-1 h-1 bg-slate-700 rounded-full overflow-hidden">
      <div 
        className="h-full bg-cyan-500 transition-all duration-300"
        style={{ width: `${((current + progress) / total) * 100}%` }}
      />
    </div>
    {isPlaying && (
      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
    )}
  </div>
);

// Scene indicator dots
const SceneIndicators = ({ scenes, currentIndex, onSelect }) => (
  <div className="flex items-center justify-center gap-1.5 mt-4">
    {scenes.map((scene, index) => (
      <button
        key={scene.id}
        onClick={() => onSelect(index)}
        className={`
          w-2 h-2 rounded-full transition-all duration-200
          ${index === currentIndex 
            ? 'bg-cyan-400 w-6' 
            : index < currentIndex 
              ? 'bg-cyan-600' 
              : 'bg-slate-600 hover:bg-slate-500'
          }
        `}
        title={scene.title}
      />
    ))}
  </div>
);

// Narration panel component
const NarrationPanel = ({ scene, isVisible }) => {
  const Icon = scene.icon;
  
  return (
    <div className={`
      fixed bottom-24 left-1/2 -translate-x-1/2 w-full max-w-3xl px-4 z-50
      transition-all duration-500 ease-out
      ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 pointer-events-none'}
    `}>
      <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-700 rounded-2xl p-6 shadow-2xl">
        {/* Scene title */}
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-cyan-500/20 rounded-lg">
            <Icon className="w-5 h-5 text-cyan-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">{scene.title}</h3>
        </div>
        
        {/* Narration text */}
        <p className="text-slate-300 leading-relaxed">
          {scene.narration}
        </p>
      </div>
    </div>
  );
};

// Control bar component
const ControlBar = ({ 
  isPlaying, 
  onPlayPause, 
  onPrevious, 
  onNext, 
  onClose,
  canGoPrevious,
  canGoNext,
  currentScene,
  totalScenes,
  progress
}) => (
  <div className="fixed bottom-0 left-0 right-0 z-50">
    <div className="bg-slate-900/95 backdrop-blur-xl border-t border-slate-700">
      <div className="max-w-5xl mx-auto px-6 py-4">
        {/* Progress */}
        <ProgressBar 
          current={currentScene} 
          total={totalScenes} 
          isPlaying={isPlaying}
          progress={progress}
        />
        
        {/* Controls */}
        <div className="flex items-center justify-between mt-4">
          {/* Left: Close */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4 mr-2" />
            Exit Walkthrough
          </Button>
          
          {/* Center: Navigation */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onPrevious}
              disabled={!canGoPrevious}
              className="border-slate-600"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <Button
              variant={isPlaying ? "secondary" : "default"}
              size="sm"
              onClick={onPlayPause}
              className={isPlaying ? "bg-slate-700" : "bg-cyan-600 hover:bg-cyan-700"}
            >
              {isPlaying ? (
                <>
                  <Pause className="w-4 h-4 mr-2" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Autoplay
                </>
              )}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={onNext}
              disabled={!canGoNext}
              className="border-slate-600"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Right: Scene indicators */}
          <div className="w-32">
            <SceneIndicators 
              scenes={SCENES} 
              currentIndex={currentScene}
              onSelect={() => {}}
            />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Highlight overlay for visual focus
const HighlightOverlay = ({ highlight, isActive }) => {
  const getHighlightPosition = () => {
    switch (highlight) {
      case 'hero':
        return 'top-20 left-1/2 -translate-x-1/2 w-full max-w-4xl';
      case 'delivery-ribbon':
      case 'delivery-stages':
        return 'top-72 left-1/2 -translate-x-1/2 w-full max-w-5xl';
      case 'travel-card':
        return 'top-[480px] left-1/2 -translate-x-1/2 w-full max-w-2xl';
      case 'travel-outcomes':
        return 'top-[520px] left-1/2 -translate-x-1/2 w-full max-w-3xl';
      default:
        return 'hidden';
    }
  };

  if (!isActive) return null;

  return (
    <div className={`
      fixed ${getHighlightPosition()} pointer-events-none z-40
      transition-all duration-500
    `}>
      <div className="absolute inset-0 border-2 border-cyan-400/50 rounded-2xl animate-pulse" />
      <div className="absolute inset-0 bg-cyan-400/5 rounded-2xl" />
    </div>
  );
};

// Main walkthrough content - embeds the landing page content
const WalkthroughContent = ({ currentScene }) => {
  const scene = SCENES[currentScene];
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 pb-40">
      {/* Hero Section */}
      <section 
        className={`py-16 transition-opacity duration-500 ${
          scene.section === 'landing' ? 'opacity-100' : 'opacity-70'
        }`}
        data-walkthrough="hero"
      >
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full mb-6">
            <Layers className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm">KATLAS Governance Demonstrators</span>
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4">
            Three Workflows, One Governance Substrate
          </h1>
          
          <p className="text-lg text-slate-400 max-w-3xl mx-auto">
            See how the same run-time operational governance pattern supports different 
            multi-actor workflows. Each demo shows the user experience at the edge and 
            the governance logic beneath it.
          </p>
        </div>
      </section>

      {/* Authorised Delivery Chain */}
      <section 
        className={`pb-8 transition-opacity duration-500 ${
          scene.section === 'delivery-chain' ? 'opacity-100' : 'opacity-70'
        }`}
        data-walkthrough="delivery-chain"
      >
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-10">
            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="text-lg font-semibold text-white mb-1">
                From idea to production with attributable governance
              </h2>
              <p className="text-sm text-slate-400">
                Who acted, under what authority, with what evidence, and why the next step was allowed
              </p>
            </div>
            
            {/* Stage Ribbon */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <div className="flex items-center justify-center flex-wrap gap-4">
                {[
                  { icon: Lightbulb, label: 'Idea Logged', color: 'cyan' },
                  { icon: FileCheck, label: 'Requirement Authorised', color: 'emerald' },
                  { icon: Users, label: 'Work Delegated', color: 'purple' },
                  { icon: GitBranch, label: 'Change Governed', color: 'amber', status: 'quarantined' },
                  { icon: Rocket, label: 'Release Allowed', color: 'rose', status: 'pending' }
                ].map((stage, index, arr) => (
                  <React.Fragment key={stage.label}>
                    <div className="flex flex-col items-center gap-2">
                      <div className={`
                        w-12 h-12 rounded-full flex items-center justify-center
                        bg-${stage.color}-500/20 border border-${stage.color}-500/50
                        ${stage.status === 'quarantined' ? 'ring-2 ring-amber-500/50' : ''}
                        ${stage.status === 'pending' ? 'opacity-60' : ''}
                      `}>
                        <stage.icon className={`w-6 h-6 text-${stage.color}-400`} />
                      </div>
                      <span className={`text-xs font-medium ${stage.status === 'pending' ? 'text-slate-500' : 'text-slate-300'}`}>
                        {stage.label}
                      </span>
                    </div>
                    {index < arr.length - 1 && (
                      <ChevronRight className="w-5 h-5 text-slate-600" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            
            {/* Business message */}
            <p className="text-center text-sm text-slate-500 mt-4 max-w-2xl mx-auto">
              Most organisations can track tasks. Far fewer can prove how an idea became an authorised requirement, 
              how work was delegated, what changed, and why a production release was allowed.
            </p>
          </div>
        </div>
      </section>

      {/* Demo Cards */}
      <section 
        className={`pb-12 transition-opacity duration-500 ${
          ['travel-demo', 'scenario-lab', 'baggage', 'other-demos'].includes(scene.section) 
            ? 'opacity-100' : 'opacity-70'
        }`}
        data-walkthrough="demos"
      >
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Travel Demo Card */}
            <div className={`
              block p-6 bg-slate-800/50 border rounded-xl transition-all
              ${scene.section === 'travel-demo' 
                ? 'border-cyan-500 ring-2 ring-cyan-500/30' 
                : 'border-slate-700'
              }
            `}>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-cyan-500/10 rounded-xl">
                  <Train className="w-8 h-8 text-cyan-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white mb-2">Assisted Travel Advisor</h3>
                  <p className="text-slate-400 text-sm mb-3">
                    See how Katlas-governed decisions protect vulnerable passengers during service disruptions.
                  </p>
                  
                  {/* Outcomes */}
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded text-emerald-400 text-xs">
                      Better Decision
                    </span>
                    <span className="px-2 py-1 bg-amber-500/20 border border-amber-500/30 rounded text-amber-400 text-xs">
                      Reduced Risk
                    </span>
                    <span className="px-2 py-1 bg-cyan-500/20 border border-cyan-500/30 rounded text-cyan-400 text-xs">
                      Attributable Evidence
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Patient Demo Card */}
            <div className={`
              block p-6 bg-slate-800/50 border rounded-xl transition-all
              ${scene.section === 'other-demos' 
                ? 'border-rose-500 ring-2 ring-rose-500/30' 
                : 'border-slate-700'
              }
            `}>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-rose-500/10 rounded-xl">
                  <Heart className="w-8 h-8 text-rose-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white mb-2">Assisted Patient Advisor</h3>
                  <p className="text-slate-400 text-sm">
                    A patient navigates a cardiology referral pathway with governed information sharing.
                  </p>
                </div>
              </div>
            </div>

            {/* CPS Demo Card */}
            <div className={`
              block p-6 bg-slate-800/50 border rounded-xl transition-all
              ${scene.section === 'other-demos' 
                ? 'border-indigo-500 ring-2 ring-indigo-500/30' 
                : 'border-slate-700'
              }
            `}>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-indigo-500/10 rounded-xl">
                  <Scale className="w-8 h-8 text-indigo-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white mb-2">Brief & Advocate Allocation</h3>
                  <p className="text-slate-400 text-sm">
                    Advocate validation, scoped brief sharing, and attributable allocation updates.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scenario Lab & Baggage Preview */}
      {(scene.section === 'scenario-lab' || scene.section === 'baggage') && (
        <section className="pb-12">
          <div className="max-w-5xl mx-auto px-6">
            <div className={`
              bg-slate-800/30 border rounded-xl p-6
              ${scene.section === 'scenario-lab' ? 'border-purple-500/50' : 'border-emerald-500/50'}
            `}>
              {scene.section === 'scenario-lab' && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <Users className="w-6 h-6 text-purple-400" />
                    <h3 className="text-xl font-semibold text-white">Scenario Lab</h3>
                  </div>
                  <p className="text-slate-400 mb-4">
                    Configure bounded scenarios for repeatable partner testing. Compare runs and view complete receipt history.
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <div className="px-4 py-2 bg-slate-800 rounded-lg border border-slate-700">
                      <span className="text-sm text-slate-300">Compare Runs</span>
                    </div>
                    <div className="px-4 py-2 bg-slate-800 rounded-lg border border-slate-700">
                      <span className="text-sm text-slate-300">Receipt History</span>
                    </div>
                  </div>
                </div>
              )}
              
              {scene.section === 'baggage' && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    {scene.id === 'who-sees-what' ? (
                      <Eye className="w-6 h-6 text-emerald-400" />
                    ) : (
                      <Package className="w-6 h-6 text-emerald-400" />
                    )}
                    <h3 className="text-xl font-semibold text-white">
                      {scene.id === 'who-sees-what' ? 'Who Sees What' : 'Essential Baggage'}
                    </h3>
                  </div>
                  <p className="text-slate-400 mb-4">
                    {scene.id === 'who-sees-what' 
                      ? 'Bounded disclosure by role. Each actor sees only what their authority permits.'
                      : 'Track custody chain with cryptographic receipts. See handoff history and current holder.'
                    }
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <div className="px-4 py-2 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                      <span className="text-sm text-emerald-400">Medical Staff View</span>
                    </div>
                    <div className="px-4 py-2 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                      <span className="text-sm text-cyan-400">Handler View</span>
                    </div>
                    <div className="px-4 py-2 bg-amber-500/10 rounded-lg border border-amber-500/30">
                      <span className="text-sm text-amber-400">Support View</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Closing Section */}
      {scene.section === 'closing' && (
        <section className="py-12 bg-slate-800/30">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Shield className="w-8 h-8 text-cyan-400" />
              <h2 className="text-2xl font-bold text-white">Runtime Governance</h2>
            </div>
            
            <p className="text-lg text-slate-300 mb-8">
              KATLAS provides runtime Custody, Authority, and Receipts across the full lifecycle.
            </p>
            
            <div className="grid grid-cols-3 gap-6">
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700">
                <Lock className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                <h4 className="font-semibold text-white mb-1">Custody</h4>
                <p className="text-sm text-slate-400">Data remains with the responsible role</p>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700">
                <Shield className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                <h4 className="font-semibold text-white mb-1">Authority</h4>
                <p className="text-sm text-slate-400">Actions checked at runtime against role and scope</p>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700">
                <Receipt className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                <h4 className="font-semibold text-white mb-1">Receipts</h4>
                <p className="text-sm text-slate-400">Every handoff produces attributable evidence</p>
              </div>
            </div>
            
            <p className="text-slate-500 mt-8">
              Across idea, execution, decision, handoff, and release — governed and attributable.
            </p>
          </div>
        </section>
      )}

      {/* Shared Governance - Shown in most scenes */}
      {!['closing'].includes(scene.section) && (
        <section className="py-12 bg-slate-800/30">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex items-center gap-2 mb-6">
              <Shield className="w-5 h-5 text-cyan-400" />
              <h2 className="text-xl font-semibold text-white">Shared Governance Pattern</h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-slate-800 rounded-lg">
                  <Lock className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">Custody</div>
                  <div className="text-xs text-slate-500">Data remains with the responsible role</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-slate-800 rounded-lg">
                  <Shield className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">Authority</div>
                  <div className="text-xs text-slate-500">Requests checked at runtime against role and scope</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-slate-800 rounded-lg">
                  <FileCheck className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">Receipts</div>
                  <div className="text-xs text-slate-500">Each handoff produces attributable evidence</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

// Main WalkthroughMode component
const WalkthroughMode = () => {
  const navigate = useNavigate();
  const [currentScene, setCurrentScene] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showNarration, setShowNarration] = useState(true);
  const intervalRef = useRef(null);
  const progressRef = useRef(null);

  const scene = SCENES[currentScene];
  const canGoPrevious = currentScene > 0;
  const canGoNext = currentScene < SCENES.length - 1;

  // Autoplay logic
  useEffect(() => {
    if (isPlaying) {
      const duration = scene.duration;
      const step = 100 / (duration / 100); // Update every 100ms
      
      progressRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 1) {
            return prev;
          }
          return prev + (step / 100);
        });
      }, 100);

      intervalRef.current = setTimeout(() => {
        if (canGoNext) {
          setCurrentScene(prev => prev + 1);
          setProgress(0);
        } else {
          setIsPlaying(false);
        }
      }, duration);
    }

    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [isPlaying, currentScene, scene.duration, canGoNext]);

  // Reset progress when scene changes
  useEffect(() => {
    setProgress(0);
    setShowNarration(false);
    const timer = setTimeout(() => setShowNarration(true), 300);
    return () => clearTimeout(timer);
  }, [currentScene]);

  const handlePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  const handlePrevious = useCallback(() => {
    if (canGoPrevious) {
      setCurrentScene(prev => prev - 1);
      setProgress(0);
    }
  }, [canGoPrevious]);

  const handleNext = useCallback(() => {
    if (canGoNext) {
      setCurrentScene(prev => prev + 1);
      setProgress(0);
    }
  }, [canGoNext]);

  const handleClose = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          handleNext();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handlePrevious();
          break;
        case 'Escape':
          handleClose();
          break;
        case 'p':
          handlePlayPause();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrevious, handleClose, handlePlayPause]);

  return (
    <div className="relative">
      {/* Walkthrough mode indicator */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-1.5 backdrop-blur-sm">
          <span className="text-cyan-400 text-sm font-medium">
            Guided Walkthrough Mode
          </span>
        </div>
      </div>

      {/* Main content */}
      <WalkthroughContent currentScene={currentScene} />

      {/* Highlight overlay */}
      <HighlightOverlay highlight={scene.highlight} isActive={true} />

      {/* Narration panel */}
      <NarrationPanel scene={scene} isVisible={showNarration} />

      {/* Control bar */}
      <ControlBar
        isPlaying={isPlaying}
        onPlayPause={handlePlayPause}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onClose={handleClose}
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
        currentScene={currentScene}
        totalScenes={SCENES.length}
        progress={progress}
      />
    </div>
  );
};

export default WalkthroughMode;
